import { Flex } from '@osmosis/tailwind-ui/components/Box';
import React from 'react';

import CTAs from '../block-ui/CTAs';
import Heading from '../block-ui/Heading';
import Subheading from '../block-ui/Subheading';
import RichText from '../block-ui/RichText';
import BlockWrapper from '../BlockWrapper';
import type { ImageGridBlockProps } from '../../../types/composer';
import {
  ctasSchema,
  movableCtaSchema,
} from '../../../lib/composer/schemas/cta';
import titleSchema from '../../../lib/composer/schemas/title';
import subtitleSchema from '../../../lib/composer/schemas/subtitle';
import imagesSchema from '../../../lib/composer/schemas/images';
import textSchema from '../../../lib/composer/schemas/text';
import themeSchema, {
  themeToTailwindClassMap,
} from '../../../lib/composer/schemas/theme';
import ImageItem from './ImageItem';
import overrideBackgroundSchema from '../../../lib/composer/schemas/backgroundColor';

type ImageGridProps = ImageGridBlockProps;

/**
 * ImageGrid block.
 */
const ImageGrid = (props: ImageGridProps) => {
  const {
    title,
    subtitle,
    text,
    images,
    theme = 'transparent',
    ctas: ctasProps,
    mcta: movableCtaProps,
    anchor,
    topHeader,
    overrideBackground,
  } = props;

  const bgTheme = themeToTailwindClassMap[theme];

  return (
    <Flex
      className={overrideBackground || bgTheme}
      id={anchor}
      data-testid="image-grid-block"
    >
      <BlockWrapper>
        <Flex className={`flex-col items-center gap-4`}>
          {title && (
            <Heading
              text={title}
              theme={theme}
              topHeader={topHeader}
              textProps="text-center"
            />
          )}
          {subtitle && (
            <Subheading
              text={subtitle}
              theme={theme}
              textProps="text-center"
              topHeader={topHeader}
            />
          )}
          {text && (
            <RichText
              html={text}
              theme={theme}
              containerProps={{ sx: { textAlign: 'center' } }}
            />
          )}
          <Flex className="flex-row items-center">
            {movableCtaProps?.caption && movableCtaProps?.position == 'top' && (
              <CTAs ctas={[movableCtaProps]} theme={theme} />
            )}
            {ctasProps && <CTAs ctas={ctasProps || []} theme={theme} />}
          </Flex>
          <Flex className={`flex-wrap justify-center gap-8`}>
            {!!images?.length &&
              images.map((img, i) => (
                <ImageItem
                  key={i}
                  image={img}
                  theme={theme}
                  outline={img.outline}
                  justify={
                    ['left', 'center', 'right'].includes(img.justify)
                      ? (img.justify as 'left' | 'center' | 'right')
                      : 'center'
                  }
                />
              ))}
          </Flex>
          {movableCtaProps?.caption &&
            movableCtaProps?.position == 'bottom' && (
              <CTAs ctas={[movableCtaProps]} theme={theme} />
            )}
        </Flex>
      </BlockWrapper>
    </Flex>
  );
};

ImageGrid.schema = {
  name: 'image-grid',
  label: 'Image Grid',
  editableProps: [
    titleSchema,
    subtitleSchema,
    textSchema,
    movableCtaSchema,
    ctasSchema,
    imagesSchema,
    themeSchema,
    overrideBackgroundSchema,
  ],
};

export default ImageGrid;
